import React, { useMemo } from 'react'

import { formatDate } from '../../../apps/professional-app/fa/common/aifeatures/utils'

import { selectBusinessEvents } from '../../../store/businessEvent/businessEvent'
import { useAppSelector } from '../../../store/hooks'

import { useBusinessEventSources } from '../../../views/Contacts/ContactBusinessEvents/hooks'
import {
  getBusinessEventSourceKey,
  getResolvedBusinessEventSourceLabel,
  getResolvedBusinessEventSourceLink,
} from '../../../views/Contacts/ContactBusinessEvents/utils'

import {
  BUSINESS_EVENT_DETAILS_KEYS_MAPPING,
  BUSINESS_EVENT_LABEL_MAP,
  BUSINESS_EVENT_SOURCE_ICON_MAPPING,
  BusinessEvent,
  ResolvedBusinessEventSource,
} from '../../../models/BusinessEvent'

import { formatEdtfString } from '../../../common/dateUtils'
import { splitCamelCase } from '../../../common/util'

import { BusinessEventIcon } from '../BusinessEventIcon/BusinessEventIcon'
import { getBusinessEventDetailsDisplayValue } from '../utils'

import './BusinessEventList.scss'

interface BusinessEventListProps {
  showSource?: boolean
  showCreatedAt?: boolean
}

export const BusinessEventList: React.FC<BusinessEventListProps> = ({ showSource = false, showCreatedAt = false }) => {
  const businessEvents = useAppSelector(selectBusinessEvents)

  const { sources } = useBusinessEventSources({ skip: !showSource })

  return (
    <div className='d-flex flex-column zocks-gap-1.25'>
      {businessEvents.map((businessEvent) => (
        <BusinessEventRow key={businessEvent.id} businessEvent={businessEvent} sources={sources} showCreatedAt={showCreatedAt} />
      ))}
    </div>
  )
}

interface BusinessEventRowProps {
  businessEvent: BusinessEvent
  sources: ResolvedBusinessEventSource[]
  showCreatedAt?: boolean
}

const BusinessEventRow: React.FC<BusinessEventRowProps> = ({ businessEvent, sources, showCreatedAt = false }) => {
  const { details, type, at: happenedAt } = businessEvent

  const source = useMemo(() => {
    const sourceKey = getBusinessEventSourceKey(businessEvent)
    const source = sources.find((e) => e.sourceKey === sourceKey)
    if (!source) {
      return null
    }
    return source
  }, [businessEvent, sources])

  const sourceLabel = source ? getResolvedBusinessEventSourceLabel(source) : null
  const sourceLink = source ? getResolvedBusinessEventSourceLink(source) : null
  const sourceIconClassName = source ? BUSINESS_EVENT_SOURCE_ICON_MAPPING[source.type] : null

  const detailsKeys = BUSINESS_EVENT_DETAILS_KEYS_MAPPING[type] ?? Object.keys(details).filter((key) => key !== 'type')
  const detailsEntries = Object.entries(details).filter(([key]) => detailsKeys.includes(key))
  const validDetailsEntries = detailsEntries.filter(([, value]) => value !== undefined && value !== null)

  return (
    <div className='d-flex align-items-start gap-2 align-self-stretch'>
      <div className='d-flex flex-column align-items-center align-self-stretch zocks-gap-1.25'>
        <BusinessEventIcon className='business-event-row-icon' />
        <div className='business-event-row-left-highlight flex-1 zocks-bg-color-gray-300' />
      </div>
      <div className='d-flex flex-column align-items-start zocks-gap-0.75 flex-1'>
        <div className='d-flex flex-column gap-1'>
          <h6>{BUSINESS_EVENT_LABEL_MAP[type]}</h6>
          <div className='d-flex align-items-center gap-1 paragraph-small'>
            {source ? (
              <>
                <span className='zocks-text-gray-600'>Source:</span>
                <i className={`bi ${sourceIconClassName} zocks-text-gray-700`} />
                {sourceLink ? (
                  <a href={sourceLink} className='zocks-text-gray-900 text-decoration-underline' target='_blank' rel='noopener noreferrer'>
                    {sourceLabel}
                  </a>
                ) : (
                  <span className='zocks-text-gray-900'>{sourceLabel}</span>
                )}
              </>
            ) : null}
            {source && showCreatedAt ? <div className='business-event-row-source-divider zocks-bg-color-gray-600' /> : null}
            {showCreatedAt ? (
              <>
                <span className='zocks-text-gray-600'>Detected:</span>
                <span className='zocks-text-gray-900'>{formatDate(businessEvent.createdAt)}</span>
              </>
            ) : null}
          </div>
        </div>
        <div className='d-flex flex-column gap-2 align-self-stretch p-3 zocks-bg-color-gray-100 rounded-2'>
          {!happenedAt && !validDetailsEntries.length ? (
            <span className='paragraph-extra-small zocks-text-gray-600 text-center'>No metadata detected.</span>
          ) : (
            <>
              <div className='d-flex'>
                <span className='paragraph-small business-event-row-details-column zocks-text-gray-700'>When</span>
                <span className='paragraph-small flex-1 bold'>{happenedAt ? formatEdtfString(happenedAt) : '-'}</span>
              </div>
              {detailsEntries.map(([key, value]) => (
                <div key={key} className='d-flex'>
                  <span className='paragraph-small business-event-row-details-column zocks-text-gray-700'>{splitCamelCase(key)}</span>
                  <span className='paragraph-small flex-1 bold'>{getBusinessEventDetailsDisplayValue(value)}</span>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
